import { FC, useEffect, useState } from 'react';
import { useGetAllProductsQuery, useGetStoresListNoCacheQuery, useUpdateInventoryStatusMutation } from '@services';
import { Grid, ListItemButton, List, Typography } from '@mui/material';
import { LoadingSpinner } from '@components';
import { JimboResponse, Product } from '@models';
import { LoadingButton } from '@mui/lab';
import { useAppDispatch } from '@hooks/redux-custom';
import { showToast } from '@store/slices/notification';
import { addErrorToLog } from '@store/slices/error-log';

type Props = {
  searchState: string;
};

interface DepotCluster {
  clusterID: string;
  clusterName: string;
  stores?: {
    id: string;
    name: string;
  }[];
}

interface ProductCluster {
  clusterName: string;
  products?: {
    id: number;
    sku: string;
    name: string;
  }[];
}

enum RangingOptions {
  OOS = 'oos',
  Snooze = 'snoozed',
  Derange = 'deranged',
  Exclude = 'exclude',
  OK = 'ok',
}

interface StoreSkuMapping {
  store_code: string;
  sku: string;
  status: string;
  ttl: number;
}

const ttl = 10;

export const AvaRangingView: FC<Props> = ({ searchState }) => {
  const { data: stores, isLoading: isStoresLoading } = useGetStoresListNoCacheQuery();
  const storesInState = stores?.filter((store) => store.address?.state === searchState);
  const [selectedDepotCluster, setSelectedDepotCluster] = useState<DepotCluster | null>();
  const [selectedProductCluster, setSelectedProductCluster] = useState<ProductCluster | null>();
  const [storesByCluster, setStoresByCluster] = useState<DepotCluster[]>([]);
  const { data: products, isLoading: isProductsLoading } = useGetAllProductsQuery();
  const [productsByCluster, setProductsByCluster] = useState<ProductCluster[]>([]);
  const [selectedFunction, setSelectedFunction] = useState<string | null>();
  const [saveRanging, setSaveRanging] = useState(false);
  const [updateInventoryStatus] = useUpdateInventoryStatusMutation();
  const [loading, setLoading] = useState(false);

  const dispatch = useAppDispatch();

  useEffect(() => {
    const updatedStoresByCluster: any[] = [];

    setLoading(true);

    if (storesInState) {
      storesInState.forEach((store) => {
        if (store.cluster) {
          store.cluster.forEach((cluster) => {
            const existingClusterIndex = updatedStoresByCluster.findIndex((item) => item.clusterID === cluster.id);

            if (existingClusterIndex === -1) {
              updatedStoresByCluster.push({
                clusterName: cluster.name,
                clusterID: cluster.id,
                stores: [{ id: store.store_code || '0', name: store.name }],
              });
            } else {
              updatedStoresByCluster[existingClusterIndex].stores.push({
                id: store.store_code || '0',
                name: store.name,
              });
            }
          });
        }
      });
    }

    setStoresByCluster(updatedStoresByCluster);
    setLoading(false);
    setSelectedDepotCluster(null);
    setSelectedProductCluster(null);
    setSelectedFunction(null);
  }, [searchState, stores]);

  useEffect(() => {
    const updatedProductsByCluster: ProductCluster[] = [];

    setLoading(true);

    if (products) {
      products.forEach((product: Product) => {
        const getCustomFieldByName = product?.custom_fields?.find((field) => field.name === 'cluster')?.value?.split(', ');

        if (getCustomFieldByName) {
          getCustomFieldByName.forEach((cluster) => {
            if (cluster === '-') {
              return;
            }

            const existingClusterIndex = updatedProductsByCluster.findIndex((item) => item.clusterName === cluster);

            if (existingClusterIndex === -1) {
              updatedProductsByCluster.push({
                clusterName: cluster,
                products: [{ id: product.id, sku: product.sku.toString() || '0', name: product.name }],
              });
            } else {
              updatedProductsByCluster[existingClusterIndex].products?.push({
                id: product.id,
                sku: product.sku.toString() || '0',
                name: product.name,
              });
            }
          });
        }
      });
    }

    setProductsByCluster(updatedProductsByCluster);
    setLoading(false);
  }, [products]);

  const handleDepotClusterClick = (clusterID: any, clusterName: any, stores?: any) => {
    setSelectedDepotCluster({ clusterID, clusterName, stores });
  };

  const handleProductClusterClick = (clusterName: any, products?: any) => {
    setSelectedProductCluster({ clusterName, products });
  };

  const getStoreSkuMappings = (): { stores: StoreSkuMapping[] } => {
    const stores = selectedDepotCluster?.stores || [];
    const products = selectedProductCluster?.products || [];
    const func = selectedFunction || '';

    const storeSkuMappings: StoreSkuMapping[] = stores.flatMap((store) =>
      products.map((product) => ({
        store_code: store.id,
        sku: product.sku,
        status: func,
        ttl: ttl,
      })),
    );

    return { stores: storeSkuMappings };
  };

  const handleSave = () => {
    setSaveRanging(true);
    const stores = getStoreSkuMappings();

    if (stores.stores.length > 0) {
      updateInventoryStatus(stores)
        .unwrap()
        .then((r) => {
          dispatch(
            showToast({
              message: 'Successfully updated inventory status for ' + selectedDepotCluster?.clusterName,
              type: 'success',
            }),
          );

          handleClose();
        })
        .catch((error: { data: JimboResponse }) => {
          dispatch(addErrorToLog(error?.data));

          dispatch(
            showToast({
              message: 'Failed to update inventory status for ' + selectedDepotCluster?.clusterName,
              type: 'error',
            }),
          );

          handleClose();
        });
    }
  };

  const handleClose = () => {
    setSaveRanging(false);
    setSelectedDepotCluster(undefined);
    setSelectedProductCluster(undefined);
    setSelectedFunction(undefined);
    setLoading(false);
  };

  return isStoresLoading || isProductsLoading || loading ? (
    <LoadingSpinner isOpen={isStoresLoading || isProductsLoading || loading} />
  ) : (
    <>
      <Grid border="1px solid" data-testid="depot-cluster-view-grid" container item xs={12} p={2}>
        <Grid container item xs={3} spacing={2} p={2} minHeight={400}>
          {storesByCluster.length !== 0 ? (
            <List
              dense
              sx={{ overflowY: 'scroll', border: '1px solid #cd9bf0', borderRadius: '4px', minWidth: '100%', maxHeight: '800px' }}
            >
              {storesByCluster.map((cluster) => (
                <ListItemButton
                  sx={{ justifyContent: 'space-between' }}
                  key={cluster.clusterID}
                  selected={selectedDepotCluster?.clusterID === cluster.clusterID}
                  onClick={() => handleDepotClusterClick(cluster.clusterID, cluster.clusterName, cluster.stores)}
                >
                  {cluster.clusterName}
                </ListItemButton>
              ))}
            </List>
          ) : (
            <Typography variant="body1" p={2}>
              No clusters available for this state{' '}
            </Typography>
          )}
        </Grid>

        <Grid container item xs={3} spacing={2} p={2} minHeight={400}>
          {productsByCluster.length !== 0 ? (
            <List
              dense
              sx={{ overflowY: 'scroll', border: '1px solid #cd9bf0', borderRadius: '4px', minWidth: '100%', maxHeight: '800px' }}
            >
              {productsByCluster
                .sort((a, b) => a.clusterName.localeCompare(b.clusterName))
                .map((cluster) => (
                  <ListItemButton
                    sx={{ justifyContent: 'space-between' }}
                    key={cluster.clusterName}
                    selected={selectedProductCluster?.clusterName === cluster.clusterName}
                    onClick={() => handleProductClusterClick(cluster.clusterName, cluster.products)}
                  >
                    {cluster.clusterName}
                  </ListItemButton>
                ))}
            </List>
          ) : (
            <Typography variant="body1" p={2}>
              No clusters available
            </Typography>
          )}
        </Grid>
        <Grid container item xs={2} spacing={2} p={2} minHeight={400}>
          <List dense sx={{ overflowY: 'scroll', border: '1px solid #cd9bf0', borderRadius: '4px', minWidth: '100%', maxHeight: '800px' }}>
            {Object.values(RangingOptions)
              .sort() // Sort options alphabetically
              .map((option) => (
                <ListItemButton
                  sx={{ justifyContent: 'space-between' }}
                  key={option}
                  selected={selectedFunction === option}
                  onClick={() => setSelectedFunction(option)}
                >
                  {option}
                </ListItemButton>
              ))}
          </List>
        </Grid>
        <Grid container item xs={4} spacing={2} p={2} minHeight={400}>
          {selectedDepotCluster?.clusterName && (
            <Grid
              container
              item
              p={2}
              m={2}
              border={'1px solid #cd9bf0'}
              borderRadius={4}
              alignItems={'center'}
              display={'flex'}
              justifyContent={'center'}
            >
              <Typography variant="subtitle1">{selectedDepotCluster?.clusterName}</Typography>
            </Grid>
          )}
          {selectedProductCluster?.clusterName && (
            <Grid
              container
              item
              p={2}
              m={2}
              border={'1px solid #cd9bf0'}
              borderRadius={4}
              alignItems={'center'}
              display={'flex'}
              justifyContent={'center'}
            >
              <Typography variant="subtitle1">{selectedProductCluster?.clusterName}</Typography>
            </Grid>
          )}
          {selectedFunction && (
            <Grid
              container
              item
              p={2}
              m={2}
              border={'1px solid #cd9bf0'}
              borderRadius={4}
              alignItems={'center'}
              display={'flex'}
              justifyContent={'center'}
            >
              <Typography variant="subtitle1">{selectedFunction}</Typography>
            </Grid>
          )}
          {selectedFunction && selectedProductCluster && selectedDepotCluster && (
            <Grid
              container
              item
              p={2}
              m={2}
              border={'1px solid #cd9bf0'}
              borderRadius={4}
              alignItems={'center'}
              display={'flex'}
              justifyContent={'center'}
            >
              <LoadingButton loading={saveRanging} variant={'outlined'} onClick={handleSave} data-testid="confirm-button">
                Apply
              </LoadingButton>
            </Grid>
          )}
        </Grid>
      </Grid>
    </>
  );
};
